import * as XLSX from 'xlsx-js-style';
import { format, parseISO, isValid } from 'date-fns';

const formatDateLong = (dateString) => {
  if (!dateString) return '';
  try {
    const d = parseISO(dateString);
    if (isValid(d)) {
      return format(d, 'MMMM dd, yyyy');
    }
    return dateString;
  } catch {
    return dateString;
  }
};

const formatTime = (timeStr) => {
  if (!timeStr || timeStr === '-' || timeStr === 'null' || timeStr === 'undefined') return '-';
  const str = String(timeStr).trim();

  // If already like "09:00 AM" or "9:00 PM"
  if (/^\d{1,2}:\d{2}(\s?[APap][Mm])$/.test(str)) {
    return str.toUpperCase();
  }

  // If like "09:00:00" or "09:00" (time only without date)
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(str)) {
    const [hStr, mStr] = str.split(':');
    let h = parseInt(hStr, 10);
    const m = mStr.padStart(2, '0');
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${String(h).padStart(2, '0')}:${m} ${ampm}`;
  }

  try {
    let d = parseISO(str);
    if (!isValid(d)) {
      d = parseISO(str.replace(' ', 'T'));
    }
    if (!isValid(d)) {
      d = new Date(str);
    }
    if (isValid(d)) {
      return format(d, 'hh:mm a');
    }
    return str;
  } catch {
    return str;
  }
};

const formatDateShort = (dateString) => {
  if (!dateString) return '';
  try {
    const d = parseISO(dateString);
    if (isValid(d)) {
      return format(d, 'MM-dd-yyyy');
    }
    return dateString;
  } catch {
    return dateString;
  }
};

// Mapper from API GridDetails item to table row structure
const mapGridDetailToRow = (item, index) => {
  const sched = Number(item.ScheduledHours ?? item.scheduledHrs ?? 0);
  const actual = Number(item.ActualHours ?? item.actualHrs ?? 0);
  const ot = Number(item.OTHours ?? item.overtimeHrs ?? 0);
  const varH = Number(item.VarianceHours ?? item.varianceHrs ?? 0);

  let otPercent = item.OTPercent ?? item.otPercent;
  if (otPercent !== undefined && otPercent !== null) {
    if (typeof otPercent === 'number') {
      otPercent = `${otPercent.toFixed(2)}%`;
    } else if (typeof otPercent === 'string' && !otPercent.includes('%')) {
      const num = Number(otPercent);
      otPercent = !isNaN(num) ? `${num.toFixed(2)}%` : otPercent;
    }
  } else {
    otPercent = actual > 0 ? `${((ot / actual) * 100).toFixed(2)}%` : '0.00%';
  }

  let otRisk = item.OTRisk || item.otRisk;
  if (!otRisk) {
    otRisk = ot > 2 ? 'High' : ot > 0 ? 'Medium' : 'Low';
  }

  let dateVal = '';
  const rawDate = item.Date || item.date || item.WeekStartDate || item.weekStartDate;
  if (rawDate) {
    dateVal = formatDateShort(rawDate);
  }

  return {
    id: `${item.EmployeeId || 'emp'}-${index}`,
    employeeId: item.EmployeeId,
    date: dateVal,
    employeeName: item.EmployeeName || '-',
    department: item.NimbleDeptName || item.DepartmentName || item.department || '-',
    jobTitle: item.NimbleJobName || item.JobName || item.jobTitle || '-',
    punchIn: formatTime(item.PunchIn || item.punchIn),
    punchOut: formatTime(item.PunchOut || item.punchOut),
    scheduledHrs: sched,
    actualHrs: actual,
    overtimeHrs: ot,
    varianceHrs: varH,
    otPercent: otPercent,
    otRisk: otRisk,
    status: item.Status || item.status || 'On Schedule',
    week: item.Week ?? item.week ?? item.WeekName,
    weekStartDate: item.WeekStartDate ?? item.weekStartDate,
    weekEndDate: item.WeekEndDate ?? item.weekEndDate,
    month: item.Month ?? item.month ?? item.MonthName,
    monthStartDate: item.MonthStartDate ?? item.monthStartDate,
    monthEndDate: item.MonthEndDate ?? item.monthEndDate,
    year: item.Year ?? item.year,
    corporationName: item.CorporationName || 'Corporation',
    pcName: item.PCName || (item.PCID ? `PC ${item.PCID}` : '-'),
    employeeCount: Number(item.EmployeeCount ?? item.employeeCount ?? 1),
  };
};

const buildTableGroupsInternal = (rawGridDetails, reportType, isMultiCorp, filters) => {
  if (!rawGridDetails || rawGridDetails.length === 0) return [];
  const mapped = rawGridDetails.map(mapGridDetailToRow);

  if (reportType === 'weekly') {
    const weeklyMap = new Map();
    mapped.forEach((rec) => {
      const weekKey = !isMultiCorp
        ? `${rec.week || 'W1'}___${rec.weekStartDate || ''}___${rec.weekEndDate || ''}`
        : `${rec.corporationName || ''}___${rec.week || 'W1'}___${rec.weekStartDate || ''}___${rec.weekEndDate || ''}`;
      if (!weeklyMap.has(weekKey)) {
        weeklyMap.set(weekKey, {
          corpName: rec.corporationName,
          weekCode: rec.week || 'W1',
          weekStartDate: rec.weekStartDate,
          weekEndDate: rec.weekEndDate,
          records: [],
        });
      }
      weeklyMap.get(weekKey).records.push(rec);
    });

    const groups = [];
    weeklyMap.forEach((val, key) => {
      let startFormatted = '';
      let endFormatted = '';
      if (val.weekStartDate) {
        try {
          const s = parseISO(val.weekStartDate);
          if (isValid(s)) startFormatted = format(s, 'MMM dd, yyyy');
        } catch { }
      }
      if (val.weekEndDate) {
        try {
          const e = parseISO(val.weekEndDate);
          if (isValid(e)) endFormatted = format(e, 'MMM dd, yyyy');
        } catch { }
      }
      const dateRangeStr = startFormatted && endFormatted ? ` (${startFormatted} – ${endFormatted})` : '';
      const headerTitle = !isMultiCorp
        ? `${val.weekCode}${dateRangeStr}`
        : `${val.corpName ? `${val.corpName} | ` : ''}${val.weekCode}${dateRangeStr}`;
      groups.push({ id: key, headerTitle, records: val.records });
    });
    return groups;
  }

  if (reportType === 'monthly') {
    const monthMap = new Map();
    mapped.forEach((rec) => {
      const monthKey = !isMultiCorp
        ? `${rec.month || 'M1'}___${rec.year || ''}___${rec.monthStartDate || ''}`
        : `${rec.corporationName || ''}___${rec.month || 'M1'}___${rec.year || ''}___${rec.monthStartDate || ''}`;
      if (!monthMap.has(monthKey)) {
        monthMap.set(monthKey, {
          corpName: rec.corporationName,
          monthCode: rec.month || 'M1',
          year: rec.year || '',
          records: [],
        });
      }
      monthMap.get(monthKey).records.push(rec);
    });

    const groups = [];
    monthMap.forEach((val, key) => {
      const rawMonth = val.monthCode;
      const yearStr = val.year ? `, ${val.year}` : '';
      const monthTitle = `${rawMonth}${yearStr}`;
      const headerTitle = !isMultiCorp
        ? monthTitle
        : `${val.corpName ? `${val.corpName} | ` : ''}${monthTitle}`;
      groups.push({ id: key, headerTitle, records: val.records });
    });
    return groups;
  }

  // Daily
  if (!isMultiCorp) {
    const dailyMap = new Map();
    mapped.forEach((rec) => {
      const key = `${rec.department}___${rec.jobTitle}`;
      if (!dailyMap.has(key)) {
        dailyMap.set(key, {
          date: rec.date || filters?.fromDate || '',
          deptName: rec.department,
          jobName: rec.jobTitle,
          records: [],
        });
      }
      dailyMap.get(key).records.push(rec);
    });

    const groups = [];
    dailyMap.forEach((val, key) => {
      groups.push({
        id: key,
        headerTitle: `Department: ${val.deptName} | Job: ${val.jobName}`,
        deptName: val.deptName,
        jobName: val.jobName,
        records: val.records,
      });
    });
    return groups;
  }

  // Multi-Corporation Daily Mode: 1st complete 1 corporation, then start another corporation
  const corpMap = new Map();
  mapped.forEach((rec) => {
    const corpKey = rec.corporationName || 'Corporation';
    if (!corpMap.has(corpKey)) {
      corpMap.set(corpKey, new Map());
    }
    const dateKey = rec.date || filters?.fromDate || '';
    const dateMap = corpMap.get(corpKey);
    if (!dateMap.has(dateKey)) {
      dateMap.set(dateKey, {
        corpName: rec.corporationName || '',
        date: dateKey,
        records: [],
      });
    }
    dateMap.get(dateKey).records.push(rec);
  });

  const groups = [];
  corpMap.forEach((dateMap, corpKey) => {
    dateMap.forEach((val, dateKey) => {
      let formattedDate = val.date;
      if (val.date) {
        try {
          formattedDate = isValid(parseISO(val.date))
            ? format(parseISO(val.date), 'MM-dd-yyyy')
            : val.date;
        } catch {
          formattedDate = val.date;
        }
      }
      groups.push({ id: `${corpKey}___${dateKey}`, headerTitle: formattedDate, dateTitle: formattedDate, corpName: val.corpName, records: val.records });
    });
  });
  return groups;
};

const computeReportHeaderLabels = ({
  filters = {},
  reportType = 'daily',
  reportTitle = 'Time and Attendance Report',
  reportData = null,
  rawGridDetails = [],
  isMultiCorp = false,
  pcNo = '-',
}) => {
  const dataRecords =
    rawGridDetails && rawGridDetails.length > 0
      ? rawGridDetails
      : reportData?.GridDetails && Array.isArray(reportData.GridDetails)
        ? reportData.GridDetails
        : [];

  const uniqueCorpsMap = new Map();
  dataRecords.forEach((r) => {
    const name = r.CorporationName || r.corpName;
    const id = r.CorporationID || r.corpId;
    if (
      name !== null &&
      name !== undefined &&
      String(name).trim() !== '' &&
      String(name).toLowerCase() !== 'null' &&
      String(name).toLowerCase() !== 'undefined'
    ) {
      const key = String(id || name).trim();
      if (!uniqueCorpsMap.has(key)) {
        uniqueCorpsMap.set(key, String(name).trim());
      }
    } else if (
      id !== null &&
      id !== undefined &&
      String(id).trim() !== '' &&
      String(id).toLowerCase() !== 'null' &&
      String(id).toLowerCase() !== 'undefined'
    ) {
      const key = String(id).trim();
      if (!uniqueCorpsMap.has(key)) {
        uniqueCorpsMap.set(key, String(id).trim());
      }
    }
  });
  const uniqueCorps = Array.from(uniqueCorpsMap.values());

  const isMultiCorpEffective = uniqueCorps.length > 0 ? uniqueCorps.length > 1 : Boolean(isMultiCorp);

  const uniquePcsMap = new Map();
  dataRecords.forEach((r) => {
    const pcId = r.PCID ?? r.pcId ?? r.ProfitCenterID;
    const pcName = r.PCName ?? r.pcName ?? r.ProfitCenterName;
    const hasValidId =
      pcId !== null &&
      pcId !== undefined &&
      String(pcId).trim() !== '' &&
      String(pcId).toLowerCase() !== 'null' &&
      String(pcId).toLowerCase() !== 'undefined';
    const hasValidName =
      pcName !== null &&
      pcName !== undefined &&
      String(pcName).trim() !== '' &&
      String(pcName).toLowerCase() !== 'null' &&
      String(pcName).toLowerCase() !== 'undefined';
    if (hasValidName || hasValidId) {
      const key = hasValidName ? String(pcName).trim() : String(pcId).trim();
      if (!uniquePcsMap.has(key)) {
        uniquePcsMap.set(key, hasValidName ? String(pcName).trim() : String(pcId).trim());
      }
    }
  });
  const uniquePcs = Array.from(uniquePcsMap.values());

  let finalCorp = '-';
  if (uniqueCorps.length > 1) {
    finalCorp = `${uniqueCorps.length} Corporations selected`;
  } else if (uniqueCorps.length === 1) {
    finalCorp = uniqueCorps[0];
  } else if (filters?.corporation && filters.corporation !== 'all' && !filters.corporation.includes('Corporations Selected')) {
    finalCorp = filters.corporation;
  }

  let finalPc = '-';
  if (uniquePcs.length > 1) {
    finalPc = `${uniquePcs.length} Profit centers selected`;
  } else if (uniquePcs.length === 1) {
    finalPc = uniquePcs[0];
  }

  let finalPcNo = '-';
  if (uniquePcs.length === 0) {
    finalPcNo = '-';
  } else if (uniquePcs.length > 1) {
    finalPcNo = 'Multiple';
  } else if (dataRecords.length > 0) {
    const match = dataRecords.find((r) => {
      const val = r.PCNo ?? r.pcNo ?? r.PCNumber ?? r.ProfitCenterNo;
      return val !== undefined && val !== null && String(val).trim() !== '' && String(val).toLowerCase() !== 'null';
    });
    if (match) {
      finalPcNo = String(match.PCNo ?? match.pcNo ?? match.PCNumber ?? match.ProfitCenterNo).trim();
    } else if (pcNo && pcNo !== 'Multiple') {
      finalPcNo = pcNo;
    }
  }

  const uniqueBrands = [
    ...new Set(
      dataRecords
        .map((r) => r?.Brand ?? r?.brand ?? r?.BrandName ?? r?.brandName ?? r?.Business ?? r?.business ?? '')
        .map((b) => String(b).trim())
        .filter((b) => b !== '' && b.toLowerCase() !== 'null' && b.toLowerCase() !== 'undefined')
    ),
  ];
  let finalBusiness = '-';
  if (uniqueBrands.length === 1) {
    finalBusiness = uniqueBrands[0];
  } else if (uniqueBrands.length > 1) {
    finalBusiness = 'Multiple';
  }

  const fromDateLong = formatDateLong(filters?.fromDate);
  const toDateLong = formatDateLong(filters?.toDate);
  const dateRangeStr = fromDateLong && toDateLong ? `${fromDateLong} To ${toDateLong}` : (filters?.fromDate || '');

  const reportTypeFormatted =
    reportType === 'daily'
      ? 'Daily'
      : reportType === 'weekly'
        ? 'Weekly'
        : reportType === 'monthly'
          ? 'Monthly'
          : reportType.charAt(0).toUpperCase() + reportType.slice(1);

  const mainTitle = isMultiCorpEffective
    ? 'Combined Time and Attendance Report'
    : reportTitle || 'Time and Attendance Report';

  return {
    finalCorpLabel: finalCorp,
    finalPcLabel: finalPc,
    finalPcNoLabel: finalPcNo,
    finalBusinessLabel: finalBusiness,
    dateRangeStr,
    reportTypeFormatted,
    mainTitle,
    isMultiCorpEffective,
  };
};

/**
 * Generate and download Excel sheet for Time and Attendance Report using Poppins font, ReportHeader Box & direct calculated values
 */
export const generateTimeAndAttendanceExcel = ({
  filters = {},
  reportType = 'daily',
  reportTitle = 'Time and Attendance Report',
  selectedFields = [],
  reportData = null,
  tableGroups = [],
  rawGridDetails = [],
  hasProfitCenter = false,
  isMultiCorp: isMultiCorpProp = false,
  pcNo = '-',
}) => {
  const rows = [];
  const formulas = new Map(); // key: `${r}_${c}`, value: formulaString

  const setFormula = (r, c, formula) => {
    if (r !== undefined && c !== undefined && c >= 0 && formula) {
      formulas.set(`${r}_${c}`, formula);
    }
  };

  const {
    finalCorpLabel,
    finalPcLabel,
    finalPcNoLabel,
    finalBusinessLabel,
    dateRangeStr,
    reportTypeFormatted,
    mainTitle,
    isMultiCorpEffective,
  } = computeReportHeaderLabels({
    filters,
    reportType,
    reportTitle,
    reportData,
    rawGridDetails,
    isMultiCorp: isMultiCorpProp,
    pcNo,
  });

  const isMultiCorp = isMultiCorpEffective;

  // -------------------------------------------------------------
  // 1. Report Header Box (Matching UI ReportHeader)
  // -------------------------------------------------------------
  // Row 0: Corporation Legal Name | [Corp Name] | [Report Title]
  // Row 1: Profit Center | [PC Name] | View by – [Type]
  // Row 2: PC NO | [PC No] |
  // Row 3: Blank separator before data table
  rows.push([isMultiCorp ? 'Corporation Legal Name' : 'Corporation/Legal Name', finalCorpLabel, mainTitle]);
  rows.push(['Profit Center', finalPcLabel, isMultiCorp ? `View by – ${reportTypeFormatted}` : '']);
  rows.push([isMultiCorp ? '' : 'PC NO', isMultiCorp ? '' : finalPcNoLabel, '']);
  rows.push(['']);

  const isDaily = reportType === 'daily';
  let activeFields = Array.isArray(selectedFields) && selectedFields.length > 0
    ? [...selectedFields]
    : (!isDaily
        ? ['scheduledHrs', 'actualHrs', 'overtimeHrs', 'varianceHrs', 'otPercent', 'otRisk']
        : (isMultiCorp
            ? ['punchIn', 'punchOut', 'scheduledHrs', 'actualHrs', 'varianceOvertimeHrs', 'otPercent', 'otRisk']
            : ['punchIn', 'punchOut', 'scheduledHrs', 'actualHrs', 'overtimeHrs', 'varianceHrs', 'status']));

  if (!isDaily || !isMultiCorp) {
    if (activeFields.includes('varianceOvertimeHrs')) {
      activeFields = activeFields.filter((f) => f !== 'varianceOvertimeHrs');
      if (!activeFields.includes('overtimeHrs')) activeFields.push('overtimeHrs');
      if (!activeFields.includes('varianceHrs')) activeFields.push('varianceHrs');
    }
  }

  const dataRecords =
    rawGridDetails && rawGridDetails.length > 0
      ? rawGridDetails
      : reportData?.GridDetails && Array.isArray(reportData.GridDetails)
        ? reportData.GridDetails
        : [];

  const hasDepartment = dataRecords.some((item) => {
    const deptName = item.NimbleDeptName ?? item.DepartmentName ?? item.deptName ?? item.Department;
    return deptName !== null && deptName !== undefined && String(deptName).trim() !== '' && String(deptName).toLowerCase() !== 'null' && String(deptName).toLowerCase() !== 'undefined';
  });
  const hasJob = dataRecords.some((item) => {
    const jobName = item.NimbleJobName ?? item.JobName ?? item.jobName ?? item.JobTitle ?? item.Job;
    return jobName !== null && jobName !== undefined && String(jobName).trim() !== '' && String(jobName).toLowerCase() !== 'null' && String(jobName).toLowerCase() !== 'undefined';
  });

  const hasEmployeeDetails = dataRecords.some((r) => Boolean(r.EmployeeName || r.EmployeeId));
  const isSummaryMultiCorp = isMultiCorp && (!hasEmployeeDetails || reportType === 'weekly' || reportType === 'monthly');

  const headerRowIndex = rows.length;
  let otPColIdx = -1;
  let finalHeaders = [];

  if (isSummaryMultiCorp) {
    // -------------------------------------------------------------
    // Multi-Corporation Summary View
    // -------------------------------------------------------------
    const isVarianceOtActive = isDaily && isMultiCorp && (activeFields.includes('varianceOvertimeHrs') || activeFields.includes('varianceHrs') || activeFields.includes('overtimeHrs'));
    let headers = [];
    if (isDaily) {
      headers = [
        'Corporation',
        ...(hasProfitCenter ? ['Profit Center'] : []),
        'Department',
        'Job Title',
        'Date',
        'Employee Count',
        ...(activeFields.includes('punchIn') ? ['Punch In'] : []),
        ...(activeFields.includes('punchOut') ? ['Punch Out'] : []),
        ...(activeFields.includes('scheduledHrs') ? ['Scheduled Hrs'] : []),
        ...(activeFields.includes('actualHrs') ? ['Actual Hrs'] : []),
        ...(isVarianceOtActive ? ['Variance Hours /OT Hrs'] : []),
        ...(activeFields.includes('otPercent') ? ['OT%'] : []),
        ...(activeFields.includes('otRisk') ? ['OT Risk'] : []),
      ];
    } else if (reportType === 'weekly') {
      headers = [
        'Corporation',
        ...(hasProfitCenter ? ['Profit Center'] : []),
        ...(hasDepartment ? ['Department'] : []),
        ...(hasJob ? ['Job Title'] : []),
        'Week',
        'Week End Date',
        'Employee Count',
        ...(activeFields.includes('scheduledHrs') ? ['Scheduled Hrs'] : []),
        ...(activeFields.includes('actualHrs') ? ['Actual Hrs'] : []),
        ...(activeFields.includes('overtimeHrs') ? ['Overtime Hrs'] : []),
        ...(activeFields.includes('varianceHrs') ? ['Variance Hours'] : []),
        ...(activeFields.includes('otPercent') ? ['OT%'] : []),
        ...(activeFields.includes('otRisk') ? ['OT Risk'] : []),
      ];
    } else {
      // Monthly
      headers = [
        'Corporation',
        ...(hasProfitCenter ? ['Profit Center'] : []),
        ...(hasDepartment ? ['Department'] : []),
        ...(hasJob ? ['Job Title'] : []),
        'Month',
        'Employee Count',
        ...(activeFields.includes('scheduledHrs') ? ['Scheduled Hrs'] : []),
        ...(activeFields.includes('actualHrs') ? ['Actual Hrs'] : []),
        ...(activeFields.includes('overtimeHrs') ? ['Overtime Hrs'] : []),
        ...(activeFields.includes('varianceHrs') ? ['Variance Hours'] : []),
        ...(activeFields.includes('otPercent') ? ['OT%'] : []),
        ...(activeFields.includes('otRisk') ? ['OT Risk'] : []),
      ];
    }

    rows.push(headers);
    finalHeaders = headers;

    const schedColIdx = headers.indexOf('Scheduled Hrs');
    const actualColIdx = headers.indexOf('Actual Hrs');
    const otColIdx = headers.indexOf('Overtime Hrs');
    const varColIdx = headers.indexOf('Variance Hours') >= 0 ? headers.indexOf('Variance Hours') : headers.indexOf('Variance Hours /OT Hrs');
    otPColIdx = headers.indexOf('OT%');

    const schedColLetter = schedColIdx >= 0 ? XLSX.utils.encode_col(schedColIdx) : null;
    const actualColLetter = actualColIdx >= 0 ? XLSX.utils.encode_col(actualColIdx) : null;
    const otColLetter = otColIdx >= 0 ? XLSX.utils.encode_col(otColIdx) : null;
    const varColLetter = varColIdx >= 0 ? XLSX.utils.encode_col(varColIdx) : null;

    const corpMap = new Map();
    dataRecords.forEach((item) => {
      const corpKey = item.CorporationID || item.CorporationName || 'Corp';
      const corpName = item.CorporationName || 'Corporation';
      const pcKey = item.PCID || item.PCName || 'All';
      const pcName = item.PCName || (item.PCID ? `PC ${item.PCID}` : '-');

      if (!corpMap.has(corpKey)) {
        corpMap.set(corpKey, {
          corpName,
          pcMap: new Map(),
        });
      }
      const corpObj = corpMap.get(corpKey);
      if (!corpObj.pcMap.has(pcKey)) {
        corpObj.pcMap.set(pcKey, {
          pcName,
          records: [],
        });
      }
      corpObj.pcMap.get(pcKey).records.push(item);
    });

    let allCorpsSched = 0;
    let allCorpsActual = 0;
    let allCorpsOT = 0;
    let allCorpsVar = 0;
    let allCorpsMaxEmp = 0;
    const allCorpTotalRowIndices = [];

    corpMap.forEach((corpObj) => {
      let corpSched = 0;
      let corpActual = 0;
      let corpOT = 0;
      let corpVar = 0;
      let corpMaxEmp = 0;
      const pcTotalRowIndices = [];

      corpObj.pcMap.forEach((pcObj) => {
        let pcSched = 0;
        let pcActual = 0;
        let pcOT = 0;
        let pcVar = 0;
        let pcMaxEmp = 0;
        const deptTotalRowIndices = [];

        // Group by Department & Job
        const deptJobMap = new Map();
        pcObj.records.forEach((rec) => {
          const deptName = rec.NimbleDeptName || rec.DepartmentName || rec.deptName || '';
          const jobName = rec.NimbleJobName || rec.JobName || rec.jobTitle || '';
          const djKey = `${deptName}___${jobName}`;
          if (!deptJobMap.has(djKey)) {
            deptJobMap.set(djKey, { deptName, jobName, records: [] });
          }
          deptJobMap.get(djKey).records.push(rec);
        });

        deptJobMap.forEach((djGroup) => {
          const hasDj = Boolean(djGroup.deptName || djGroup.jobName);
          let djSched = 0;
          let djActual = 0;
          let djOT = 0;
          let djVar = 0;
          let djMaxEmp = 0;
          const djDataRowIndices = [];

          if (reportType === 'weekly') {
            djGroup.records.forEach((rec) => {
              const sched = Number(rec.ScheduledHours ?? rec.ScheduledHrs ?? rec.scheduledHrs ?? 0);
              const actual = Number(rec.ActualHours ?? rec.actualHrs ?? 0);
              const ot = Number(rec.OTHours ?? rec.overtimeHrs ?? 0);
              const varH = Number(rec.VarianceHours ?? rec.varianceHrs ?? 0);
              const empCount = Number(rec.EmployeeCount ?? rec.employeeCount ?? 1);

              djSched += sched;
              djActual += actual;
              djOT += ot;
              djVar += varH;
              if (empCount > djMaxEmp) djMaxEmp = empCount;

              let otPStr = '0.00%';
              if (rec.OTPercent !== undefined && rec.OTPercent !== null) {
                otPStr = `${Number(rec.OTPercent).toFixed(2)}%`;
              } else if (actual > 0) {
                otPStr = `${((ot / actual) * 100).toFixed(2)}%`;
              }

              let dateStr = '';
              const endD = rec.WeekEndDate || rec.Date || rec.WeekStartDate;
              if (endD) {
                dateStr = formatDateShort(endD);
              }

              const currRowIdx = rows.length;
              const currRowExcel = currRowIdx + 1;
              if (otPColIdx >= 0 && actualColLetter && otColLetter) {
                setFormula(currRowIdx, otPColIdx, `IF(${actualColLetter}${currRowExcel}>0,(${otColLetter}${currRowExcel}/${actualColLetter}${currRowExcel})*100,0)`);
              }

              const row = [
                corpObj.corpName,
                ...(hasProfitCenter ? [pcObj.pcName] : []),
                ...(hasDepartment ? [rec.NimbleDeptName || rec.DepartmentName || rec.deptName || '-'] : []),
                ...(hasJob ? [rec.NimbleJobName || rec.JobName || rec.jobTitle || '-'] : []),
                rec.Week || 'W01',
                dateStr,
                empCount,
                ...(activeFields.includes('scheduledHrs') ? [Number(sched.toFixed(2))] : []),
                ...(activeFields.includes('actualHrs') ? [Number(actual.toFixed(2))] : []),
                ...(activeFields.includes('overtimeHrs') ? [Number(ot.toFixed(2))] : []),
                ...(activeFields.includes('varianceHrs') ? [Number(varH.toFixed(2))] : []),
                ...(activeFields.includes('otPercent') ? [otPStr] : []),
                ...(activeFields.includes('otRisk')
                  ? [rec.OTRisk || (ot > 2 ? 'High' : ot > 0 ? 'Medium' : 'Low')]
                  : []),
              ];
              djDataRowIndices.push(currRowIdx);
              rows.push(row);
            });
          } else {
            // monthly or daily
            djGroup.records.forEach((rec) => {
              const sched = Number(rec.ScheduledHours ?? rec.ScheduledHrs ?? rec.scheduledHrs ?? 0);
              const actual = Number(rec.ActualHours ?? rec.actualHrs ?? 0);
              const ot = Number(rec.OTHours ?? rec.overtimeHrs ?? 0);
              const varH = Number(rec.VarianceHours ?? rec.varianceHrs ?? 0);
              const empCount = Number(rec.EmployeeCount ?? rec.employeeCount ?? 1);

              djSched += sched;
              djActual += actual;
              djOT += ot;
              djVar += varH;
              if (empCount > djMaxEmp) djMaxEmp = empCount;

              let otPStr = '0.00%';
              if (rec.OTPercent !== undefined && rec.OTPercent !== null) {
                otPStr = `${Number(rec.OTPercent).toFixed(2)}%`;
              } else if (actual > 0) {
                otPStr = `${((ot / actual) * 100).toFixed(2)}%`;
              }

              let dateStr = '';
              const endD = rec.WeekEndDate || rec.Date || rec.WeekStartDate;
              if (endD) {
                dateStr = formatDateShort(endD);
              }

              const currRowIdx = rows.length;
              const currRowExcel = currRowIdx + 1;
              if (otPColIdx >= 0 && actualColLetter && otColLetter) {
                setFormula(currRowIdx, otPColIdx, `IF(${actualColLetter}${currRowExcel}>0,(${otColLetter}${currRowExcel}/${actualColLetter}${currRowExcel})*100,0)`);
              }

              const row = [
                corpObj.corpName,
                ...(hasProfitCenter ? [pcObj.pcName] : []),
                ...(reportType === 'monthly' && hasDepartment ? [rec.NimbleDeptName || rec.DepartmentName || rec.deptName || '-'] : []),
                ...(reportType === 'monthly' && hasJob ? [rec.NimbleJobName || rec.JobName || rec.jobTitle || '-'] : []),
                ...(isDaily ? [rec.NimbleDeptName || rec.DepartmentName || rec.deptName || '-', rec.NimbleJobName || rec.JobName || rec.jobTitle || '-'] : []),
                ...(reportType === 'monthly' ? [rec.Year ? `${rec.Month || 'Jan'},${rec.Year}` : (rec.Month || 'Jan')] : []),
                ...(isDaily ? [dateStr] : []),
                empCount,
                ...(isDaily && activeFields.includes('punchIn') ? [formatTime(rec.PunchIn || rec.punchIn)] : []),
                ...(isDaily && activeFields.includes('punchOut') ? [formatTime(rec.PunchOut || rec.punchOut)] : []),
                ...(activeFields.includes('scheduledHrs') ? [Number(sched.toFixed(2))] : []),
                ...(activeFields.includes('actualHrs') ? [Number(actual.toFixed(2))] : []),
                ...(isVarianceOtActive ? [Number(varH.toFixed(2))] : []),
                ...(!isDaily && activeFields.includes('overtimeHrs') ? [Number(ot.toFixed(2))] : []),
                ...(!isDaily && activeFields.includes('varianceHrs') ? [Number(varH.toFixed(2))] : []),
                ...(activeFields.includes('otPercent') ? [otPStr] : []),
                ...(activeFields.includes('otRisk')
                  ? [rec.OTRisk || (ot > 2 ? 'High' : ot > 0 ? 'Medium' : 'Low')]
                  : []),
              ];
              djDataRowIndices.push(currRowIdx);
              rows.push(row);
            });
          }

          if (hasDj && deptJobMap.size > 1 && reportType !== 'monthly' && reportType !== 'weekly') {
            // Department Total Row
            const djOtPercent = djActual > 0 ? `${((djOT / djActual) * 100).toFixed(2)}%` : '0.00%';
            const djOtRisk = djOT > djActual * 0.1 ? 'High' : djOT > 0 ? 'Medium' : 'Low';
            
            const djTotalRowIdx = rows.length;
            const djTotalRowExcel = djTotalRowIdx + 1;

            if (djDataRowIndices.length > 0) {
              const firstRowExcel = djDataRowIndices[0] + 1;
              const lastRowExcel = djDataRowIndices[djDataRowIndices.length - 1] + 1;
              if (schedColIdx >= 0 && schedColLetter) setFormula(djTotalRowIdx, schedColIdx, `SUM(${schedColLetter}${firstRowExcel}:${schedColLetter}${lastRowExcel})`);
              if (actualColIdx >= 0 && actualColLetter) setFormula(djTotalRowIdx, actualColIdx, `SUM(${actualColLetter}${firstRowExcel}:${actualColLetter}${lastRowExcel})`);
              if (otColIdx >= 0 && otColLetter) setFormula(djTotalRowIdx, otColIdx, `SUM(${otColLetter}${firstRowExcel}:${otColLetter}${lastRowExcel})`);
              if (varColIdx >= 0 && varColLetter) setFormula(djTotalRowIdx, varColIdx, `SUM(${varColLetter}${firstRowExcel}:${varColLetter}${lastRowExcel})`);
            }
            if (otPColIdx >= 0 && actualColLetter && otColLetter) {
              setFormula(djTotalRowIdx, otPColIdx, `IF(${actualColLetter}${djTotalRowExcel}>0,(${otColLetter}${djTotalRowExcel}/${actualColLetter}${djTotalRowExcel})*100,0)`);
            }

            const djTotalRow = [
              corpObj.corpName,
              ...(hasProfitCenter ? [pcObj.pcName] : []),
              ...(reportType === 'monthly' && hasDepartment ? [`${djGroup.deptName || djGroup.jobName} Total`] : []),
              ...(reportType === 'monthly' && hasJob ? [''] : []),
              ...(reportType === 'weekly' && hasDepartment ? [`${djGroup.deptName || djGroup.jobName} Total`] : []),
              ...(reportType === 'weekly' && hasJob ? [''] : []),
              ...(reportType === 'weekly' ? ['', ''] : []),
              ...(reportType === 'monthly' ? [''] : []),
              ...(isDaily ? [`${djGroup.deptName || djGroup.jobName} Total`] : []),
              djMaxEmp,
              ...(isDaily && activeFields.includes('punchIn') ? ['-'] : []),
              ...(isDaily && activeFields.includes('punchOut') ? ['-'] : []),
              ...(activeFields.includes('scheduledHrs') ? [Number(djSched.toFixed(2))] : []),
              ...(activeFields.includes('actualHrs') ? [Number(djActual.toFixed(2))] : []),
              ...(isVarianceOtActive ? [Number(djVar.toFixed(2))] : []),
              ...(!isDaily && activeFields.includes('overtimeHrs') ? [Number(djOT.toFixed(2))] : []),
              ...(!isDaily && activeFields.includes('varianceHrs') ? [Number(djVar.toFixed(2))] : []),
              ...(activeFields.includes('otPercent') ? [djOtPercent] : []),
              ...(activeFields.includes('otRisk') ? [djOtRisk] : []),
            ];
            deptTotalRowIndices.push(djTotalRowIdx);
            rows.push(djTotalRow);
          } else {
            deptTotalRowIndices.push(...djDataRowIndices);
          }

          pcSched += djSched;
          pcActual += djActual;
          pcOT += djOT;
          pcVar += djVar;
          if (djMaxEmp > pcMaxEmp) pcMaxEmp = djMaxEmp;
        });

        if (hasProfitCenter) {
          const pcOtPercent = pcActual > 0 ? `${((pcOT / pcActual) * 100).toFixed(2)}%` : '0.00%';
          const pcOtRisk = pcOT > pcActual * 0.1 ? 'High' : pcOT > 0 ? 'Medium' : 'Low';
          
          const pcTotalRowIdx = rows.length;
          const pcTotalRowExcel = pcTotalRowIdx + 1;

          if (deptTotalRowIndices.length > 0) {
            if (schedColIdx >= 0 && schedColLetter) setFormula(pcTotalRowIdx, schedColIdx, `SUM(${deptTotalRowIndices.map((idx) => `${schedColLetter}${idx + 1}`).join(',')})`);
            if (actualColIdx >= 0 && actualColLetter) setFormula(pcTotalRowIdx, actualColIdx, `SUM(${deptTotalRowIndices.map((idx) => `${actualColLetter}${idx + 1}`).join(',')})`);
            if (otColIdx >= 0 && otColLetter) setFormula(pcTotalRowIdx, otColIdx, `SUM(${deptTotalRowIndices.map((idx) => `${otColLetter}${idx + 1}`).join(',')})`);
            if (varColIdx >= 0 && varColLetter) setFormula(pcTotalRowIdx, varColIdx, `SUM(${deptTotalRowIndices.map((idx) => `${varColLetter}${idx + 1}`).join(',')})`);
          }
          if (otPColIdx >= 0 && actualColLetter && otColLetter) {
            setFormula(pcTotalRowIdx, otPColIdx, `IF(${actualColLetter}${pcTotalRowExcel}>0,(${otColLetter}${pcTotalRowExcel}/${actualColLetter}${pcTotalRowExcel})*100,0)`);
          }

          const pcTotalRow = [
            corpObj.corpName,
            `${pcObj.pcName === '-' ? corpObj.corpName : pcObj.pcName} Total`,
            ...(reportType === 'monthly' && hasDepartment ? [''] : []),
            ...(reportType === 'monthly' && hasJob ? [''] : []),
            ...(reportType === 'weekly' && hasDepartment ? [''] : []),
            ...(reportType === 'weekly' && hasJob ? [''] : []),
            ...(reportType === 'weekly' ? ['', ''] : []),
            ...(reportType === 'monthly' ? [''] : []),
            ...(isDaily ? [''] : []),
            pcMaxEmp,
            ...(isDaily && activeFields.includes('punchIn') ? ['-'] : []),
            ...(isDaily && activeFields.includes('punchOut') ? ['-'] : []),
            ...(activeFields.includes('scheduledHrs') ? [Number(pcSched.toFixed(2))] : []),
            ...(activeFields.includes('actualHrs') ? [Number(pcActual.toFixed(2))] : []),
            ...(isVarianceOtActive ? [Number(pcVar.toFixed(2))] : []),
            ...(!isDaily && activeFields.includes('overtimeHrs') ? [Number(pcOT.toFixed(2))] : []),
            ...(!isDaily && activeFields.includes('varianceHrs') ? [Number(pcVar.toFixed(2))] : []),
            ...(activeFields.includes('otPercent') ? [pcOtPercent] : []),
            ...(activeFields.includes('otRisk') ? [pcOtRisk] : []),
          ];
          pcTotalRowIndices.push(pcTotalRowIdx);
          rows.push(pcTotalRow);
        } else {
          pcTotalRowIndices.push(...deptTotalRowIndices);
        }

        corpSched += pcSched;
        corpActual += pcActual;
        corpOT += pcOT;
        corpVar += pcVar;
        if (pcMaxEmp > corpMaxEmp) corpMaxEmp = pcMaxEmp;
      });

      const corpOtPercent = corpActual > 0 ? `${((corpOT / corpActual) * 100).toFixed(2)}%` : '0.00%';
      const corpOtRisk = corpOT > corpActual * 0.1 ? 'High' : corpOT > 0 ? 'Medium' : 'Low';
      
      const corpTotalRowIdx = rows.length;
      const corpTotalRowExcel = corpTotalRowIdx + 1;

      if (pcTotalRowIndices.length > 0) {
        if (schedColIdx >= 0 && schedColLetter) setFormula(corpTotalRowIdx, schedColIdx, `SUM(${pcTotalRowIndices.map((idx) => `${schedColLetter}${idx + 1}`).join(',')})`);
        if (actualColIdx >= 0 && actualColLetter) setFormula(corpTotalRowIdx, actualColIdx, `SUM(${pcTotalRowIndices.map((idx) => `${actualColLetter}${idx + 1}`).join(',')})`);
        if (otColIdx >= 0 && otColLetter) setFormula(corpTotalRowIdx, otColIdx, `SUM(${pcTotalRowIndices.map((idx) => `${otColLetter}${idx + 1}`).join(',')})`);
        if (varColIdx >= 0 && varColLetter) setFormula(corpTotalRowIdx, varColIdx, `SUM(${pcTotalRowIndices.map((idx) => `${varColLetter}${idx + 1}`).join(',')})`);
      }
      if (otPColIdx >= 0 && actualColLetter && otColLetter) {
        setFormula(corpTotalRowIdx, otPColIdx, `IF(${actualColLetter}${corpTotalRowExcel}>0,(${otColLetter}${corpTotalRowExcel}/${actualColLetter}${corpTotalRowExcel})*100,0)`);
      }

      const corpTotalRow = [
        `${corpObj.corpName} Total`,
        ...(hasProfitCenter ? ['Grand Total'] : []),
        ...(reportType === 'monthly' && hasDepartment ? [''] : []),
        ...(reportType === 'monthly' && hasJob ? [''] : []),
        ...(reportType === 'weekly' && hasDepartment ? [''] : []),
        ...(reportType === 'weekly' && hasJob ? [''] : []),
        ...(reportType === 'weekly' ? ['', ''] : []),
        ...(reportType === 'monthly' ? [''] : []),
        ...(isDaily ? [''] : []),
        corpMaxEmp,
        ...(isDaily && activeFields.includes('punchIn') ? ['-'] : []),
        ...(isDaily && activeFields.includes('punchOut') ? ['-'] : []),
        ...(activeFields.includes('scheduledHrs') ? [Number(corpSched.toFixed(2))] : []),
        ...(activeFields.includes('actualHrs') ? [Number(corpActual.toFixed(2))] : []),
        ...(isVarianceOtActive ? [Number(corpVar.toFixed(2))] : []),
        ...(!isDaily && activeFields.includes('overtimeHrs') ? [Number(corpOT.toFixed(2))] : []),
        ...(!isDaily && activeFields.includes('varianceHrs') ? [Number(corpVar.toFixed(2))] : []),
        ...(activeFields.includes('otPercent') ? [corpOtPercent] : []),
        ...(activeFields.includes('otRisk') ? [corpOtRisk] : []),
      ];
      allCorpTotalRowIndices.push(corpTotalRowIdx);
      rows.push(corpTotalRow);

      allCorpsSched += corpSched;
      allCorpsActual += corpActual;
      allCorpsOT += corpOT;
      allCorpsVar += corpVar;
      allCorpsMaxEmp += corpMaxEmp;
    });

    if (corpMap.size > 1) {
      const allOtPercent =
        allCorpsActual > 0 ? `${((allCorpsOT / allCorpsActual) * 100).toFixed(2)}%` : '0.00%';
      const allOtRisk = allCorpsOT > allCorpsActual * 0.1 ? 'High' : allCorpsOT > 0 ? 'Medium' : 'Low';
      
      const allTotalRowIdx = rows.length;
      const allTotalRowExcel = allTotalRowIdx + 1;

      if (allCorpTotalRowIndices.length > 0) {
        if (schedColIdx >= 0 && schedColLetter) setFormula(allTotalRowIdx, schedColIdx, `SUM(${allCorpTotalRowIndices.map((idx) => `${schedColLetter}${idx + 1}`).join(',')})`);
        if (actualColIdx >= 0 && actualColLetter) setFormula(allTotalRowIdx, actualColIdx, `SUM(${allCorpTotalRowIndices.map((idx) => `${actualColLetter}${idx + 1}`).join(',')})`);
        if (otColIdx >= 0 && otColLetter) setFormula(allTotalRowIdx, otColIdx, `SUM(${allCorpTotalRowIndices.map((idx) => `${otColLetter}${idx + 1}`).join(',')})`);
        if (varColIdx >= 0 && varColLetter) setFormula(allTotalRowIdx, varColIdx, `SUM(${allCorpTotalRowIndices.map((idx) => `${varColLetter}${idx + 1}`).join(',')})`);
      }
      if (otPColIdx >= 0 && actualColLetter && otColLetter) {
        setFormula(allTotalRowIdx, otPColIdx, `IF(${actualColLetter}${allTotalRowExcel}>0,(${otColLetter}${allTotalRowExcel}/${actualColLetter}${allTotalRowExcel})*100,0)`);
      }

      const allCorpsTotalRow = [
        'All Corporations Total',
        ...(hasProfitCenter ? ['Grand Total'] : []),
        ...(reportType === 'monthly' && hasDepartment ? [''] : []),
        ...(reportType === 'monthly' && hasJob ? [''] : []),
        ...(reportType === 'weekly' && hasDepartment ? [''] : []),
        ...(reportType === 'weekly' && hasJob ? [''] : []),
        ...(reportType === 'weekly' ? ['', ''] : []),
        ...(reportType === 'monthly' ? [''] : []),
        ...(isDaily ? [''] : []),
        allCorpsMaxEmp,
        ...(isDaily && activeFields.includes('punchIn') ? ['-'] : []),
        ...(isDaily && activeFields.includes('punchOut') ? ['-'] : []),
        ...(activeFields.includes('scheduledHrs') ? [Number(allCorpsSched.toFixed(2))] : []),
        ...(activeFields.includes('actualHrs') ? [Number(allCorpsActual.toFixed(2))] : []),
        ...(isVarianceOtActive ? [Number(allCorpsVar.toFixed(2))] : []),
        ...(!isDaily && activeFields.includes('overtimeHrs') ? [Number(allCorpsOT.toFixed(2))] : []),
        ...(!isDaily && activeFields.includes('varianceHrs') ? [Number(allCorpsVar.toFixed(2))] : []),
        ...(activeFields.includes('otPercent') ? [allOtPercent] : []),
        ...(activeFields.includes('otRisk') ? [allOtRisk] : []),
      ];
      rows.push(allCorpsTotalRow);
    }
  } else {
    // -------------------------------------------------------------
    // Single Corporation View (or Detailed Multi-Corp Daily)
    // -------------------------------------------------------------
    const isVarianceOtActive = isDaily && isMultiCorp && (activeFields.includes('varianceOvertimeHrs') || activeFields.includes('varianceHrs') || activeFields.includes('overtimeHrs'));

    const headers = [
      ...(isDaily && isMultiCorp ? ['Corporation'] : []),
      ...(isDaily && isMultiCorp && hasProfitCenter ? ['Profit Center'] : []),
      ...(isDaily && !isMultiCorp ? ['Date'] : []),
      'Employee Name',
      ...((reportType === 'weekly' && isMultiCorp) || (isDaily && isMultiCorp) ? ['Department', 'Job Title'] : []),
      ...(isDaily && activeFields.includes('punchIn') ? ['Punch In'] : []),
      ...(isDaily && activeFields.includes('punchOut') ? ['Punch Out'] : []),
      ...(activeFields.includes('scheduledHrs') ? ['Scheduled Hrs'] : []),
      ...(activeFields.includes('actualHrs') ? ['Actual Hrs'] : []),
      ...(isVarianceOtActive ? ['Variance Hours /OT Hrs'] : []),
      ...(!isVarianceOtActive && activeFields.includes('overtimeHrs') ? ['Overtime Hrs'] : []),
      ...(!isVarianceOtActive && activeFields.includes('varianceHrs') ? ['Variance Hours'] : []),
      ...(!isDaily && activeFields.includes('otPercent') ? ['OT %'] : []),
      ...(!isDaily && activeFields.includes('otRisk') ? ['OT Risk'] : []),
      ...(isDaily && activeFields.includes('status') ? ['Status'] : []),
    ];

    finalHeaders = headers;

    const schedColIdx = headers.indexOf('Scheduled Hrs');
    const actualColIdx = headers.indexOf('Actual Hrs');
    const otColIdx = headers.indexOf('Overtime Hrs');
    const varColIdx = headers.indexOf('Variance Hours') >= 0 ? headers.indexOf('Variance Hours') : headers.indexOf('Variance Hours /OT Hrs');
    otPColIdx = headers.indexOf('OT %');

    const schedColLetter = schedColIdx >= 0 ? XLSX.utils.encode_col(schedColIdx) : null;
    const actualColLetter = actualColIdx >= 0 ? XLSX.utils.encode_col(actualColIdx) : null;
    const otColLetter = otColIdx >= 0 ? XLSX.utils.encode_col(otColIdx) : null;
    const varColLetter = varColIdx >= 0 ? XLSX.utils.encode_col(varColIdx) : null;

    const groups =
      tableGroups && tableGroups.length > 0
        ? tableGroups
        : buildTableGroupsInternal(dataRecords, reportType, isMultiCorp, filters);

    let grandSched = 0;
    let grandActual = 0;
    let grandOt = 0;
    let grandVar = 0;
    const groupTotalRowIndices = [];

    groups.forEach((group) => {
      // Subheader Row with Department & Job
      rows.push([group.headerTitle]);
      // Column Header Row
      rows.push(headers);

      let groupSched = 0;
      let groupActual = 0;
      let groupOt = 0;
      let groupVar = 0;
      const startDataRow = rows.length + 1; // 1-based

      group.records.forEach((r) => {
        const sched = Number(r.scheduledHrs) || 0;
        const actual = Number(r.actualHrs) || 0;
        const ot = Number(r.overtimeHrs) || 0;
        const varH = Number(r.varianceHrs) || 0;

        groupSched += sched;
        groupActual += actual;
        groupOt += ot;
        groupVar += varH;

        const currRowIdx = rows.length;
        const currRowExcel = currRowIdx + 1;

        if (varColIdx >= 0 && actualColLetter && schedColLetter) {
          setFormula(currRowIdx, varColIdx, `${actualColLetter}${currRowExcel}-${schedColLetter}${currRowExcel}`);
        }
        if (otPColIdx >= 0 && actualColLetter && otColLetter) {
          setFormula(currRowIdx, otPColIdx, `IF(${actualColLetter}${currRowExcel}>0,(${otColLetter}${currRowExcel}/${actualColLetter}${currRowExcel})*100,0)`);
        }

        const row = [
          ...(isDaily && isMultiCorp ? [r.corporationName || '-'] : []),
          ...(isDaily && isMultiCorp && hasProfitCenter ? [r.pcName || '-'] : []),
          ...(isDaily && !isMultiCorp ? [r.date || ''] : []),
          r.employeeName || '-',
          ...((reportType === 'weekly' && isMultiCorp) || (isDaily && isMultiCorp) ? [r.department || '-', r.jobTitle || '-'] : []),
          ...(isDaily && activeFields.includes('punchIn') ? [r.punchIn || '-'] : []),
          ...(isDaily && activeFields.includes('punchOut') ? [r.punchOut || '-'] : []),
          ...(activeFields.includes('scheduledHrs') ? [Number(sched.toFixed(2))] : []),
          ...(activeFields.includes('actualHrs') ? [Number(actual.toFixed(2))] : []),
          ...(isVarianceOtActive ? [Number(varH.toFixed(2))] : []),
          ...(!isVarianceOtActive && activeFields.includes('overtimeHrs') ? [Number(ot.toFixed(2))] : []),
          ...(!isVarianceOtActive && activeFields.includes('varianceHrs') ? [Number(varH.toFixed(2))] : []),
          ...(!isDaily && activeFields.includes('otPercent') ? [r.otPercent || '0.00%'] : []),
          ...(!isDaily && activeFields.includes('otRisk') ? [r.otRisk || '-'] : []),
          ...(isDaily && activeFields.includes('status') ? [r.status || 'On Schedule'] : []),
        ];
        rows.push(row);
      });
      const endDataRow = rows.length; // 1-based

      grandSched += groupSched;
      grandActual += groupActual;
      grandOt += groupOt;
      grandVar += groupVar;

      // Total Row with pre-computed values
      const otPStr =
        groupActual > 0 ? `${((groupOt / groupActual) * 100).toFixed(2)}%` : '0.00%';
      const otRStr = groupOt > 2 ? 'High' : groupOt > 0 ? 'Medium' : 'Low';

      const totalRowIdx = rows.length;
      const totalRowExcel = totalRowIdx + 1;

      if (schedColIdx >= 0 && schedColLetter) setFormula(totalRowIdx, schedColIdx, `SUM(${schedColLetter}${startDataRow}:${schedColLetter}${endDataRow})`);
      if (actualColIdx >= 0 && actualColLetter) setFormula(totalRowIdx, actualColIdx, `SUM(${actualColLetter}${startDataRow}:${actualColLetter}${endDataRow})`);
      if (otColIdx >= 0 && otColLetter) setFormula(totalRowIdx, otColIdx, `SUM(${otColLetter}${startDataRow}:${otColLetter}${endDataRow})`);
      if (varColIdx >= 0 && varColLetter) setFormula(totalRowIdx, varColIdx, `SUM(${varColLetter}${startDataRow}:${varColLetter}${endDataRow})`);
      if (otPColIdx >= 0 && actualColLetter && otColLetter) {
        setFormula(totalRowIdx, otPColIdx, `IF(${actualColLetter}${totalRowExcel}>0,(${otColLetter}${totalRowExcel}/${actualColLetter}${totalRowExcel})*100,0)`);
      }

      const totalRow = [
        ...(isDaily && isMultiCorp ? [''] : []),
        ...(isDaily && isMultiCorp && hasProfitCenter ? [''] : []),
        ...(isDaily && !isMultiCorp ? [''] : []),
        'Total',
        ...((reportType === 'weekly' && isMultiCorp) || (isDaily && isMultiCorp) ? ['', ''] : []),
        ...(isDaily && activeFields.includes('punchIn') ? [''] : []),
        ...(isDaily && activeFields.includes('punchOut') ? [''] : []),
        ...(activeFields.includes('scheduledHrs') ? [Number(groupSched.toFixed(2))] : []),
        ...(activeFields.includes('actualHrs') ? [Number(groupActual.toFixed(2))] : []),
        ...(isVarianceOtActive ? [Number(groupVar.toFixed(2))] : []),
        ...(!isVarianceOtActive && activeFields.includes('overtimeHrs') ? [Number(groupOt.toFixed(2))] : []),
        ...(!isVarianceOtActive && activeFields.includes('varianceHrs') ? [Number(groupVar.toFixed(2))] : []),
        ...(!isDaily && activeFields.includes('otPercent') ? [otPStr] : []),
        ...(!isDaily && activeFields.includes('otRisk') ? [otRStr] : []),
        ...(isDaily && activeFields.includes('status') ? [''] : []),
      ];
      groupTotalRowIndices.push(totalRowIdx);
      rows.push(totalRow);
      rows.push(['']); // Blank line between groups
    });

    if (groups.length > 1) {
      const grandOtPStr =
        grandActual > 0 ? `${((grandOt / grandActual) * 100).toFixed(2)}%` : '0.00%';
      const grandOtRStr = grandOt > grandActual * 0.1 ? 'High' : grandOt > 0 ? 'Medium' : 'Low';

      const grandTotalRowIdx = rows.length;
      const grandTotalRowExcel = grandTotalRowIdx + 1;

      if (groupTotalRowIndices.length > 0) {
        if (schedColIdx >= 0 && schedColLetter) setFormula(grandTotalRowIdx, schedColIdx, `SUM(${groupTotalRowIndices.map((idx) => `${schedColLetter}${idx + 1}`).join(',')})`);
        if (actualColIdx >= 0 && actualColLetter) setFormula(grandTotalRowIdx, actualColIdx, `SUM(${groupTotalRowIndices.map((idx) => `${actualColLetter}${idx + 1}`).join(',')})`);
        if (otColIdx >= 0 && otColLetter) setFormula(grandTotalRowIdx, otColIdx, `SUM(${groupTotalRowIndices.map((idx) => `${otColLetter}${idx + 1}`).join(',')})`);
        if (varColIdx >= 0 && varColLetter) setFormula(grandTotalRowIdx, varColIdx, `SUM(${groupTotalRowIndices.map((idx) => `${varColLetter}${idx + 1}`).join(',')})`);
      }
      if (otPColIdx >= 0 && actualColLetter && otColLetter) {
        setFormula(grandTotalRowIdx, otPColIdx, `IF(${actualColLetter}${grandTotalRowExcel}>0,(${otColLetter}${grandTotalRowExcel}/${actualColLetter}${grandTotalRowExcel})*100,0)`);
      }

      const grandTotalRow = [
        ...(isDaily && isMultiCorp ? [''] : []),
        ...(isDaily && isMultiCorp && hasProfitCenter ? [''] : []),
        ...(isDaily && !isMultiCorp ? [''] : []),
        'Grand Total',
        ...((reportType === 'weekly' && isMultiCorp) || (isDaily && isMultiCorp) ? ['', ''] : []),
        ...(isDaily && activeFields.includes('punchIn') ? [''] : []),
        ...(isDaily && activeFields.includes('punchOut') ? [''] : []),
        ...(activeFields.includes('scheduledHrs') ? [Number(grandSched.toFixed(2))] : []),
        ...(activeFields.includes('actualHrs') ? [Number(grandActual.toFixed(2))] : []),
        ...(isVarianceOtActive ? [Number(grandVar.toFixed(2))] : []),
        ...(!isVarianceOtActive && activeFields.includes('overtimeHrs') ? [Number(grandOt.toFixed(2))] : []),
        ...(!isVarianceOtActive && activeFields.includes('varianceHrs') ? [Number(grandVar.toFixed(2))] : []),
        ...(!isDaily && activeFields.includes('otPercent') ? [grandOtPStr] : []),
        ...(!isDaily && activeFields.includes('otRisk') ? [grandOtRStr] : []),
        ...(isDaily && activeFields.includes('status') ? [''] : []),
      ];
      rows.push(grandTotalRow);
      rows.push(['']);
    }
  }

  // -------------------------------------------------------------
  // 3. Create Worksheet & Apply Strict Typography & Styles
  // -------------------------------------------------------------
  const worksheet = XLSX.utils.aoa_to_sheet(rows);

  // Apply Dynamic Formulas to Cells
  formulas.forEach((formula, key) => {
    const [rStr, cStr] = key.split('_');
    const r = parseInt(rStr, 10);
    const c = parseInt(cStr, 10);
    const cellRef = XLSX.utils.encode_cell({ r, c });
    if (worksheet[cellRef]) {
      worksheet[cellRef].f = formula;
    }
  });
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1');
  const maxCols = range.e.c;

  // Auto-calculate column widths dynamically based on max cell length + padding
  const colWidths = [];
  for (let c = 0; c <= maxCols + 2; c++) {
    let maxLength = 12;
    rows.forEach((r) => {
      const val = r[c];
      if (val !== null && val !== undefined) {
        const len = String(val).length;
        if (len > maxLength) maxLength = Math.min(len, 38);
      }
    });
    colWidths.push({ wch: maxLength + 3 });
  }
  worksheet['!cols'] = colWidths;

  // -------------------------------------------------------------
  // Style Report Header Box (Rows 0, 1, 2)
  // -------------------------------------------------------------
  const rightLabelCol = Math.max(3, maxCols - 2);
  const rightValCol = rightLabelCol + 1;

  // Set Right side values into worksheet
  const r0_rightLbl = XLSX.utils.encode_cell({ r: 0, c: rightLabelCol });
  const r0_rightVal = XLSX.utils.encode_cell({ r: 0, c: rightValCol });
  worksheet[r0_rightLbl] = { v: 'Date', t: 's' };
  worksheet[r0_rightVal] = { v: dateRangeStr, t: 's' };

  const r1_rightLbl = XLSX.utils.encode_cell({ r: 1, c: rightLabelCol });
  const r1_rightVal = XLSX.utils.encode_cell({ r: 1, c: rightValCol });
  worksheet[r1_rightLbl] = { v: 'Business', t: 's' };
  worksheet[r1_rightVal] = { v: finalBusinessLabel, t: 's' };

  const r2_rightLbl = XLSX.utils.encode_cell({ r: 2, c: rightLabelCol });
  const r2_rightVal = XLSX.utils.encode_cell({ r: 2, c: rightValCol });
  worksheet[r2_rightLbl] = { v: '', t: 's' };
  worksheet[r2_rightVal] = { v: '', t: 's' };

  // Fill empty cells in rows 0, 1, 2 for border and background consistency
  for (let r = 0; r <= 2; r++) {
    for (let c = 0; c <= maxCols; c++) {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (!worksheet[ref]) {
        worksheet[ref] = { v: '', t: 's' };
      }
    }
  }

  // Merges for the ReportHeader Box
  const merges = [];
  if (rightLabelCol - 1 >= 2) {
    merges.push({ s: { r: 0, c: 2 }, e: { r: 0, c: rightLabelCol - 1 } });
    merges.push({ s: { r: 1, c: 2 }, e: { r: 1, c: rightLabelCol - 1 } });
    merges.push({ s: { r: 2, c: 2 }, e: { r: 2, c: rightLabelCol - 1 } });
  }
  if (maxCols >= rightValCol) {
    merges.push({ s: { r: 0, c: rightValCol }, e: { r: 0, c: maxCols } });
    merges.push({ s: { r: 1, c: rightValCol }, e: { r: 1, c: maxCols } });
    merges.push({ s: { r: 2, c: rightValCol }, e: { r: 2, c: maxCols } });
  }

  const borderD9 = {
    top: { style: 'thin', color: { rgb: 'D9D9D9' } },
    bottom: { style: 'thin', color: { rgb: 'D9D9D9' } },
    left: { style: 'thin', color: { rgb: 'D9D9D9' } },
    right: { style: 'thin', color: { rgb: 'D9D9D9' } },
  };

  const labelStyle = {
    font: { name: 'Poppins', bold: true, sz: 8, color: { rgb: '374151' } },
    fill: { fgColor: { rgb: 'F9FAFB' } }, // #F9FAFB Light Gray
    alignment: { vertical: 'center', horizontal: 'left' },
    border: borderD9,
  };

  const valueStyle = {
    font: { name: 'Poppins', bold: true, sz: 8, color: { rgb: '111827' } },
    fill: { fgColor: { rgb: 'FFFFFF' } },
    alignment: { vertical: 'center', horizontal: 'left' },
    border: borderD9,
  };

  const centerTitleStyle = {
    font: { name: 'Poppins', bold: true, sz: 11, color: { rgb: '111827' } },
    fill: { fgColor: { rgb: 'FFFFFF' } },
    alignment: { vertical: 'center', horizontal: 'center' },
    border: borderD9,
  };

  const centerSubTitleStyle = {
    font: { name: 'Poppins', bold: true, sz: 9, color: { rgb: '111827' } },
    fill: { fgColor: { rgb: 'FFFFFF' } },
    alignment: { vertical: 'center', horizontal: 'center' },
    border: borderD9,
  };

  const centerEmptyStyle = {
    fill: { fgColor: { rgb: 'FFFFFF' } },
    border: borderD9,
  };

  // Apply styles across rows 0, 1, 2
  for (let r = 0; r <= 2; r++) {
    for (let c = 0; c <= maxCols; c++) {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (!worksheet[ref]) continue;

      if (c === 0 || c === rightLabelCol) {
        worksheet[ref].s = labelStyle;
      } else if (c === 1 || c >= rightValCol) {
        worksheet[ref].s = valueStyle;
      } else {
        // Center block
        if (r === 0) {
          worksheet[ref].s = centerTitleStyle;
        } else if (r === 1) {
          worksheet[ref].s = centerSubTitleStyle;
        } else {
          worksheet[ref].s = centerEmptyStyle;
        }
      }
    }
  }

  // Table Rows Styling (Section headers, Column headers, Data, Totals)
  for (let r = headerRowIndex; r <= range.e.r; r++) {
    const firstCellVal = rows[r] ? rows[r][0] : null;
    const isHeaderRow =
      firstCellVal === 'Corporation' ||
      firstCellVal === 'Date' ||
      firstCellVal === 'Employee Name' ||
      (rows[r] && (rows[r].includes('Actual Hrs') || rows[r].includes('Scheduled Hrs')));

    const isSubheader =
      rows[r] &&
      rows[r].length === 1 &&
      typeof firstCellVal === 'string' &&
      firstCellVal.trim() !== '' &&
      !isHeaderRow;

    const isGrandTotalRow =
      rows[r] &&
      (rows[r].includes('All Corporations') ||
        rows[r].includes('All Corporations Total') ||
        rows[r].includes('Grand Total') ||
        (typeof firstCellVal === 'string' &&
          (firstCellVal.includes('All Corporations') || firstCellVal === 'Grand Total')) ||
        (rows[r][1] && String(rows[r][1]).includes('Grand Total')));

    const isTotalRow =
      isGrandTotalRow ||
      (rows[r] &&
        (rows[r].includes('Total') ||
          rows[r].some((cell) => typeof cell === 'string' && cell.toLowerCase().includes('total'))));

    if (isSubheader) {
      merges.push({ s: { r, c: 0 }, e: { r, c: maxCols } });
    }

    for (let c = 0; c <= range.e.c; c++) {
      const cellRef = XLSX.utils.encode_cell({ r, c });
      if (!worksheet[cellRef]) continue;

      const cellValStr = String(worksheet[cellRef].v || '');
      const isOtCol =
        c === otPColIdx ||
        cellValStr.includes('%') ||
        (worksheet[cellRef].f && worksheet[cellRef].f.includes('*100'));

      // Ensure OT % column has numeric type with 2 decimals and "%" symbol format
      if (isOtCol && !isHeaderRow && !isSubheader) {
        let numVal = null;
        if (typeof worksheet[cellRef].v === 'number') {
          numVal = Number(worksheet[cellRef].v.toFixed(2));
        } else if (typeof worksheet[cellRef].v === 'string') {
          const parsed = parseFloat(worksheet[cellRef].v.replace('%', ''));
          if (!isNaN(parsed)) {
            numVal = Number(parsed.toFixed(2));
          }
        }
        if (numVal !== null) {
          worksheet[cellRef].t = 'n';
          worksheet[cellRef].v = numVal;
          worksheet[cellRef].z = '0.00"%"';
        }
      } else if (!isHeaderRow && !isSubheader && typeof worksheet[cellRef].v === 'number') {
        worksheet[cellRef].z = '0.00';
      }

      if (isSubheader) {
        worksheet[cellRef].s = {
          font: { name: 'Poppins', bold: true, sz: 8.5, color: { rgb: '0F172A' } },
          alignment: { vertical: 'center', horizontal: 'center' },
        };
      } else if (isHeaderRow) {
        worksheet[cellRef].s = {
          font: { name: 'Poppins', bold: true, sz: 7.5, color: { rgb: '000000' } },
          fill: { fgColor: { rgb: 'EFEFEF' } }, // #EFEFEF Light Gray
          alignment: { vertical: 'center', horizontal: 'center' },
          border: {
            top: { style: 'thin', color: { rgb: 'C8C8C8' } },
            bottom: { style: 'thin', color: { rgb: 'C8C8C8' } },
            left: { style: 'thin', color: { rgb: 'C8C8C8' } },
            right: { style: 'thin', color: { rgb: 'C8C8C8' } },
          },
        };
      } else if (isGrandTotalRow) {
        worksheet[cellRef].s = {
          font: { name: 'Poppins', bold: true, sz: 8, color: { rgb: '000000' } },
          fill: { fgColor: { rgb: 'E2E8F0' } }, // #E2E8F0 Slate 200 (Matches UI Grand Total)
          alignment: {
            vertical: 'center',
            horizontal: typeof worksheet[cellRef].v === 'number' ? 'right' : 'center',
          },
          border: {
            top: { style: 'thin', color: { rgb: '94A3B8' } },
            bottom: { style: 'double', color: { rgb: '475569' } },
            left: { style: 'thin', color: { rgb: 'CBD5E1' } },
            right: { style: 'thin', color: { rgb: 'CBD5E1' } },
          },
        };
      } else if (isTotalRow) {
        worksheet[cellRef].s = {
          font: { name: 'Poppins', bold: true, sz: 8, color: { rgb: '000000' } },
          fill: { fgColor: { rgb: 'F1F5F9' } }, // #F1F5F9 Slate 100 (Matches UI Subtotal)
          alignment: {
            vertical: 'center',
            horizontal: typeof worksheet[cellRef].v === 'number' ? 'right' : 'center',
          },
          border: {
            top: { style: 'thin', color: { rgb: 'CBD5E1' } },
            bottom: { style: 'thin', color: { rgb: 'CBD5E1' } },
            left: { style: 'thin', color: { rgb: 'CBD5E1' } },
            right: { style: 'thin', color: { rgb: 'CBD5E1' } },
          },
        };
      } else {
        const cellValue = worksheet[cellRef].v;
        const isNegativeNum = typeof cellValue === 'number' && cellValue < 0;

        worksheet[cellRef].s = {
          font: {
            name: 'Poppins',
            sz: 7.5,
            color: { rgb: isNegativeNum ? 'FF0000' : '222222' },
          },
          alignment: {
            vertical: 'center',
            horizontal: typeof cellValue === 'number' ? 'right' : 'center',
          },
          border: {
            top: { style: 'thin', color: { rgb: 'E0E0E0' } },
            bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
            left: { style: 'thin', color: { rgb: 'E0E0E0' } },
            right: { style: 'thin', color: { rgb: 'E0E0E0' } },
          },
        };
      }
    }
  }

  if (finalHeaders && finalHeaders.length > 0) {
    worksheet['!cols'] = finalHeaders.map((h) => {
      if (h === 'Corporation' || h === 'Company') return { wch: 28 };
      if (h === 'Department') return { wch: 20 };
      if (h === 'Job Title') return { wch: 20 };
      if (h === 'Week') return { wch: 8 };
      if (h === 'Week End Date' || h === 'Date') return { wch: 14 };
      if (h === 'Employee Name') return { wch: 24 };
      if (h === 'Employee Count') return { wch: 14 };
      return { wch: 14 };
    });
  }

  worksheet['!merges'] = merges;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Time and Attendance');

  const filename = `Time_And_Attendance_Report_${reportTypeFormatted}.xlsx`;
  XLSX.writeFile(workbook, filename);
};
